import React, { useMemo } from 'react'
import { Badge, OverlayTrigger, Tooltip } from 'react-bootstrap'

import { ColumnDef, createColumnHelper } from '@tanstack/react-table'

import { PredictedTrafficBadge } from 'components/PredictedTrafficBadge/PredictedTrafficBadge'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { ContactCompanyType } from 'models/contact_companies'
import { menu_ingredients } from 'models/menu_ingredients'
import { BiFoodMenu } from 'react-icons/bi'
import { featureFlagService } from 'utils/featureFlagService'
import {
  formatInteger,
  formatPercentage,
  formatUsd,
} from '../../../utils/formatting'
import { getPathFromUrl } from '../../../utils/getPathFromUrl'
import { DataTableColumnSort } from '../../DataTable/DataTableSorting/DataTableSortingStore'

import { OverflownText } from '../../OverflownText'
import { Anchor } from '../../UI/Anchor'
import { LinkCell } from '../CommonTableCells/LinkCell'
import { TruncatedLinkCell } from '../CommonTableCells/TruncatedLinkCell'
import { createTableColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { TaglistCell } from 'components/FbUI/StagePill'
import apiService from 'services/api'
import { PhoneCell } from '../CommonTableCells/PhoneCell'
import { DoorProxyReport } from 'models/door_reports'
import { DOORS_CRM_REPORT_COLUMNS } from 'components/DataExpansionComponents/Doors/doorsReportsColumnsInfo'
import { CrmReportItem } from 'models/reports'
import { getCommonDoorCRMReportColumns } from 'components/DataExpansionComponents/Common/Reports/DoorCRM/DoorCRMReportColumns'
import { getCommonChainCRMReportColumns } from 'components/DataExpansionComponents/Common/Reports/ChainCRM/ChainCRMReportColumns'

export function useDoorsTableColumnsV2(
  setMenuModal: React.Dispatch<
    React.SetStateAction<
      | {
          doorId: number
          doorName?: string
          itemsIds?: number[]
          menuLength?: number
        }
      | undefined
    >
  >,
  isChainTable: boolean,
  chainFilter: number | undefined,
  selectedProducts: number[],
  sorting: DataTableColumnSort[] | undefined,
  inCampaigns?: boolean
): ColumnDef<DoorProxyReport>[] {
  const api = apiService()
  const featureFlag = featureFlagService()
  const { preferences } = usePreferences()
  const columnHelper = createColumnHelper<DoorProxyReport>()
  const tooltips: Record<string, unknown> = preferences?.tooltips || {}
  const doorsColumnHelper = createTableColumnHelper({
    columns: DOORS_CRM_REPORT_COLUMNS,
    tooltips: tooltips,
  })
  const { data: tags } = api.useGetTags()

  const columns = useMemo(() => {
    let titleCols = [
      columnHelper.accessor('name', {
        ...doorsColumnHelper.retrieveColumn('restaurant_name', {
          meta: {
            isEditable: false,
            isFirstColumn: true,
          },
        }),
        header: () => null,
        cell: (info) => {
          const val =
            info.row.original.name ||
            info.row.original.restaurant.restaurant_name ||
            '-'

          return (
            <TruncatedLinkCell
              text={val}
              to={`/restaurant/${info.row.original.restaurant.firstbite_id}`}
            />
          )
        },
        size: 250,
      }),
      columnHelper.accessor('restaurant.chain_name', {
        ...doorsColumnHelper.retrieveColumn('chain_name'),
        size: 250,
        cell: (info) => {
          const name = info.row.original.restaurant.chain_name
          const id = info.row.original.chain_proxy?.chain?.id
          if (!name) return '-'

          return (
            <TruncatedLinkCell
              text={name}
              to={`/chain/${preferences?.company_type_prefix_map[ContactCompanyType.Chain]}${id}`}
            />
          )
        },
      }),
      columnHelper.accessor('restaurant.chain_count', {
        ...doorsColumnHelper.retrieveColumn('chain_count', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 150,
        cell: (info) =>
          info.getValue() ? formatInteger(info.getValue()!) : '1',
      }),
      columnHelper.accessor('restaurant.cuisine_50', {
        ...doorsColumnHelper.retrieveColumn('cuisine_50'),
        size: 150,
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('country', {
        ...doorsColumnHelper.retrieveColumn('country'),
        size: 100,
        cell: (info) =>
          info.row.original.country ||
          info.row.original.restaurant.country ||
          '-',
      }),
      columnHelper.accessor('address', {
        ...doorsColumnHelper.retrieveColumn('address'),
        size: 200,
        cell: (info) =>
          info.row.original.address ||
          info.row.original.restaurant.address ||
          '-',
      }),
      columnHelper.accessor('city', {
        ...doorsColumnHelper.retrieveColumn('city'),
        size: 200,
        cell: (info) =>
          info.row.original.city || info.row.original.restaurant.city || '-',
      }),
      columnHelper.accessor('state', {
        ...doorsColumnHelper.retrieveColumn('state'),
        size: 100,
        cell: (info) =>
          info.row.original.state || info.row.original.restaurant.state || '-',
      }),
      columnHelper.accessor('zipcode', {
        ...doorsColumnHelper.retrieveColumn('zip'),
        size: 100,
        cell: (info) =>
          info.row.original.zipcode ||
          info.row.original.restaurant.address_info.zip ||
          '-',
      }),
      columnHelper.accessor('total_ltv_sum', {
        ...doorsColumnHelper.retrieveColumn('total_ltv', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 200,
        cell: (info) => {
          const value = info.getValue()

          return value ? formatUsd(value) : '-'
        },
      }),
      columnHelper.accessor('brand_ltv_sum', {
        ...doorsColumnHelper.retrieveColumn('brand_ltv', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 150,
        cell: (info) => {
          const value = info.getValue()

          return value ? formatUsd(value) : '-'
        },
      }),
      columnHelper.accessor('revenue_ltv_sum', {
        ...doorsColumnHelper.retrieveColumn('revenue_ltv', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 150,
        cell: (info) => {
          const value = info.getValue()

          return value ? formatUsd(value) : '-'
        },
      }),
      columnHelper.accessor('one_year_total_value', {
        ...doorsColumnHelper.retrieveColumn('one_year_total_value', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 150,
        cell: (info) => {
          const value = info.getValue()

          return value ? formatUsd(value) : '-'
        },
      }),
      columnHelper.accessor('taro', {
        ...doorsColumnHelper.retrieveColumn('taro', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 150,
        cell: (info) => {
          const value = info.getValue()

          return value ? formatUsd(value) : '-'
        },
      }),
      columnHelper.accessor('tabo', {
        ...doorsColumnHelper.retrieveColumn('tabo', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 150,
        cell: (info) => {
          const value = info.getValue()

          return value ? formatUsd(value) : '-'
        },
      }),
      columnHelper.accessor('pounds_per_year_sum', {
        ...doorsColumnHelper.retrieveColumn('pounds_per_year', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 150,
        cell: (info) =>
          info.getValue() ? formatInteger(info.getValue()) : '-',
      }),
    ]

    if (!featureFlag.shouldShowDoorMenu) {
      titleCols = titleCols.filter((col) => col.id !== 'menu')
    }

    let cols = [
      columnHelper.accessor('restaurant.avg_rating', {
        ...doorsColumnHelper.retrieveColumn('avg_rating', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 150,
        cell: (info) => info.getValue()?.toFixed(2) || '-',
      }),
      columnHelper.accessor('restaurant.total_reviews_count', {
        ...doorsColumnHelper.retrieveColumn('total_reviews_count', {
          meta: {
            rightAlign: true,
          },
        }),
        header: 'Total Reviews',
        size: 150,
        cell: (info) => formatInteger(info.getValue()) || '-',
      }),
      columnHelper.accessor('restaurant.expense_category', {
        ...doorsColumnHelper.retrieveColumn('expense_category'),
        size: 100,
        cell: (info) => {
          const value = info.getValue()
          if (value === 'nan' || !value) {
            return '-'
          } else {
            return value
          }
        },
      }),
      columnHelper.accessor('restaurant.velocity_group', {
        ...doorsColumnHelper.retrieveColumn('velocity_group'),
        size: 200,
        cell: (info) => (
          <PredictedTrafficBadge value={info.getValue() ?? null} />
        ),
      }),
      columnHelper.accessor('door_crm_report.taglist', {
        ...doorsColumnHelper.retrieveColumn('company_tags'),
        size: 200,
        cell: (info) => {
          const tagsIds: CrmReportItem[] = info.getValue()
          const filteredTags = tags?.filter((tag) =>
            tagsIds?.find((t) => t.id === tag.id)
          )
          return <TaglistCell taglist={filteredTags} />
        },
      }),
      columnHelper.accessor('chain_proxy.chain.instagram.url', {
        ...doorsColumnHelper.retrieveColumn('instagram_url'),
        cell: (info) => {
          const value = info.row.original?.chain_proxy?.chain?.instagram?.url
          if (!value) return '-'

          let url = ''
          try {
            url = getPathFromUrl(value)
          } catch (e) {
            return '-'
          }

          return (
            <Anchor href={'//' + value} target="_blank">
              @{url}
            </Anchor>
          )
        },
      }),
      columnHelper.accessor('chain_proxy.chain.instagram.followers', {
        ...doorsColumnHelper.retrieveColumn('instagram_followers'),
        cell: (info) => {
          const value =
            info.row.original.chain_proxy?.chain.instagram?.followers
          if (!value) return '-'
          return formatInteger(value)
        },
      }),
      columnHelper.accessor('chain_proxy.chain.instagram.following', {
        ...doorsColumnHelper.retrieveColumn('instagram_following', {
          meta: {
            rightAlign: true,
          },
        }),
        cell: (info) => {
          const value =
            info.row.original.chain_proxy?.chain.instagram?.following
          if (!value) return '-'
          return formatInteger(value)
        },
      }),
      columnHelper.accessor('chain_proxy.chain.instagram.posts', {
        ...doorsColumnHelper.retrieveColumn('instagram_posts', {
          meta: {
            rightAlign: true,
          },
        }),
        cell: (info) => {
          const value = info.row.original.chain_proxy?.chain.instagram?.posts
          if (!value) return '-'
          return formatInteger(value)
        },
      }),
      columnHelper.accessor('chain_proxy.chain.instagram.text_percentile', {
        ...doorsColumnHelper.retrieveColumn('instagram_text_percentile'),
        size: 200,
        cell: (info) => (
          <PredictedTrafficBadge value={info.getValue() ?? null} />
        ),
      }),
      columnHelper.display({
        ...doorsColumnHelper.retrieveColumn('reputation_data'),
        size: 200,
        cell: (info) => {
          const { michelin_id, eater_id, infatuation_id } =
            info?.row?.original?.restaurant || {}
          const reputationPlatforms = [
            michelin_id && 'Michelin',
            eater_id && 'Eater',
            infatuation_id && 'Infatuation',
          ].filter(Boolean)

          if (reputationPlatforms.length === 0) return '-'
          return (
            <div
              style={{
                gap: 2,
                display: 'flex',
                flexWrap: 'wrap',
              }}
            >
              {reputationPlatforms.map((platform, index) => (
                <Badge key={index} className="!bg-green-500">
                  {platform}
                </Badge>
              ))}
            </div>
          )
        },
      }),
      columnHelper.accessor('restaurant.michelin_stars', {
        ...doorsColumnHelper.retrieveColumn('michelin_stars'),
        size: 150,
        cell: (info) => {
          const value = info.getValue()
          if (value === undefined || value === null) return '-'
          return value
        },
      }),
      columnHelper.display({
        ...doorsColumnHelper.retrieveColumn('delivery_platforms'),
        size: 200,
        cell: (info) => {
          const { has_doordash_id, has_ubereats_id, has_grubhub_id } =
            info.row?.original?.restaurant || {}
          const platforms = [
            has_doordash_id && 'Doordash',
            has_ubereats_id && 'Uber Eats',
            has_grubhub_id && 'Grubhub',
          ].filter(Boolean)

          if (platforms.length === 0) return '-'
          return (
            <div
              style={{
                gap: 2,
                display: 'flex',
                flexWrap: 'wrap',
              }}
            >
              {platforms.map((platform, index) => (
                <Badge key={index} bg="info">
                  {platform}
                </Badge>
              ))}
            </div>
          )
        },
      }),
      columnHelper.accessor('restaurant.domain', {
        ...doorsColumnHelper.retrieveColumn('domain'),
        size: 200,
        cell: (info) => {
          const val =
            info.row.original.domain || info.row.original.restaurant.domain
          return val ? (
            <Anchor href={'//' + val} target="_blank">
              {val}
            </Anchor>
          ) : (
            '-'
          )
        },
      }),
      columnHelper.accessor('restaurant.website', {
        ...doorsColumnHelper.retrieveColumn('website'),
        size: 100,
        cell: (info) => (info.getValue() ? <LinkCell info={info} /> : '-'),
      }),
      columnHelper.accessor('phone_number', {
        ...doorsColumnHelper.retrieveColumn('phone'),
        size: 180,
        cell: (info) => {
          const phone =
            info.row.original.phone_number || info.row.original.restaurant.phone
          return <PhoneCell phoneNumber={phone} />
        },
      }),
      columnHelper.display({
        ...doorsColumnHelper.retrieveColumn('menu'),
        size: 100,
        cell: (info) => {
          const items = info.row.original?.search_items?.map(
            (item) => item.item_id
          )

          const menuLength = info.row.original?.restaurant?.menu_size ?? 0

          if (!menuLength)
            return (
              <div className="ml-1">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Unavailable</Tooltip>}
                >
                  <div className="max-w-max">
                    <BiFoodMenu size={20} className="text-gray-400" />
                  </div>
                </OverlayTrigger>
              </div>
            )

          return (
            <div className="ml-1">
              <BiFoodMenu
                size={20}
                onClick={() =>
                  setMenuModal({
                    doorId: info.row.original.restaurant.firstbite_id,
                    doorName: info.row.original.restaurant.restaurant_name,
                    itemsIds: items,
                    menuLength,
                  })
                }
                className="cursor-pointer"
              />
            </div>
          )
        },
      }),
      columnHelper.accessor('restaurant.menu_size', {
        ...doorsColumnHelper.retrieveColumn('menu_size'),
        cell: (info) => {
          return info.getValue() ? formatInteger(info.getValue()) : '-'
        },
        size: 100,
      }),
      columnHelper.accessor('menu_matches', {
        ...doorsColumnHelper.retrieveColumn('menu_matches', {
          meta: {
            rightAlign: true,
          },
        }),
        size: 110,
        cell: (info) => {
          return info.getValue() ? formatInteger(info.getValue()) : '-'
        },
      }),
      columnHelper.accessor('restaurant.address_info.dmanamecbsa', {
        ...doorsColumnHelper.retrieveColumn('dmanamecbsa'),
        size: 250,
        cell: (info) => {
          const value = info.getValue()
          if (value === 'NaN' || !value) {
            return '-'
          } else {
            return value
          }
        },
      }),
      columnHelper.accessor('chain_proxy.chain.hq_state1', {
        ...doorsColumnHelper.retrieveColumn('hq_state1'),
        size: 100,
        cell: (info) => {
          const value = info.getValue()
          return value || '-'
        },
      }),
      columnHelper.accessor('chain_proxy.chain.hq_city1', {
        ...doorsColumnHelper.retrieveColumn('hq_city1'),
        size: 100,
        cell: (info) => {
          const value = info.getValue()
          return value || '-'
        },
      }),
      columnHelper.accessor('chain_proxy.chain.hq_state2', {
        ...doorsColumnHelper.retrieveColumn('hq_state2'),
        size: 200,
        cell: (info) => {
          const value = info.getValue()
          return value || '-'
        },
      }),
      columnHelper.accessor('chain_proxy.chain.hq_city2', {
        ...doorsColumnHelper.retrieveColumn('hq_city2'),
        size: 100,
        cell: (info) => {
          const value = info.getValue()
          return value || '-'
        },
      }),
      columnHelper.accessor('restaurant.menu_url', {
        ...doorsColumnHelper.retrieveColumn('menu_url'),
        size: 100,
        cell: (info) => (info.getValue() ? <LinkCell info={info} /> : '-'),
      }),
      columnHelper.accessor('restaurant.google_place_url', {
        ...doorsColumnHelper.retrieveColumn('google_place_url'),
        size: 150,
        cell: (info) => (info.getValue() ? <LinkCell info={info} /> : '-'),
      }),
      ...getCommonDoorCRMReportColumns(columnHelper, sorting, preferences),
    ]

    if (!featureFlag.enableInstagremData) {
      cols = cols.filter((col) => !col.id?.includes('instagram'))
    }

    if (!featureFlag.shouldShowReputationData) {
      cols = cols.filter(
        (col) => !['reputation_data', 'michelin_stars'].includes(col?.id ?? '')
      )
    }

    if (!featureFlag.enableHQStateColumn) {
      cols = cols.filter(
        (col) =>
          col.id !== 'hq_state1' &&
          col.id !== 'hq_state2' &&
          col.id !== 'hq_city1' &&
          col.id !== 'hq_city2'
      )
    }

    const extraCols = [
      columnHelper.accessor('restaurant.ingredients_matrix', {
        ...doorsColumnHelper.retrieveColumn('ingredients_matrix'),
        size: 500,
        cell: (info) => {
          const ingredients = info.row.original?.restaurant?.ingredients_matrix
          const display: string[] = []

          if (!ingredients) return null

          const ingredientsValuesWhitelist = menu_ingredients.map(
            (item) => item.value
          )

          Object.entries(ingredients)
            .filter(([key]) => ingredientsValuesWhitelist.includes(key))
            .map(([key, value]) => {
              if (Number(value) > 0) display.push(key)
            })

          return <OverflownText>{display.join(', ')}</OverflownText>
        },
      }),
      columnHelper.accessor(
        'restaurant.address_info.income_medianhouseholdincome_hh_cnt',
        {
          ...doorsColumnHelper.retrieveColumn(
            'income_medianhouseholdincome_hh_cnt'
          ),
          cell: (info) => formatUsd(info.getValue()),
        }
      ),
      columnHelper.accessor('restaurant.address_info.pop_sqmi', {
        ...doorsColumnHelper.retrieveColumn('pop_sqmi'),
        cell: (info) => formatInteger(info.getValue() ?? 0) + '/mi²',
      }),
      columnHelper.display({
        ...doorsColumnHelper.retrieveColumn('hh_gt100k'),
        cell: (info) => {
          const value =
            (info?.row?.original?.restaurant?.address_info
              ?.income_100000to149999_hh_pct +
              info?.row?.original?.restaurant?.address_info
                ?.income_150000to199999_hh_pct +
              info?.row?.original?.restaurant?.address_info
                ?.income_200000ormore_hh_pct) *
            100
          return formatPercentage(value ?? 0) + '%'
        },
      }),
      columnHelper.accessor(
        'restaurant.address_info.employment_unemployed_rate_county_level_only_pct',
        {
          ...doorsColumnHelper.retrieveColumn(
            'employment_unemployed_rate_county_level_only_pct'
          ),
          cell: (info) => formatInteger((info.getValue() ?? 0) * 100) + '%',
        }
      ),
      ...getCommonChainCRMReportColumns(
        columnHelper as any,
        sorting,
        preferences
      ),
    ]

    if (isChainTable) {
      titleCols = titleCols.filter(
        (el) =>
          el.id !== 'cuisine_50' &&
          el.id !== 'chain_name' &&
          el.id !== 'chain_count'
      )
      cols = cols.filter((el) => el.id !== 'expense_category')
    }

    if (!featureFlag.shouldShowMenuSize) {
      cols = cols.filter((col) => col.id !== 'menu_size')
    }

    if (chainFilter) {
      return [...titleCols, ...cols, ...extraCols]
    }

    return [...titleCols, ...cols]
  }, [
    selectedProducts,
    featureFlag.shouldShowMenuSize,
    featureFlag.enableInstagremData,
    featureFlag.shouldShowReputationData,
    sorting,
    preferences,
    inCampaigns,
  ])

  return columns as ColumnDef<DoorProxyReport>[]
}
